import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogBody,
} from "@/components/ui/alert-dialog";
import {
  FileText,
  History,
  Loader2,
  Pencil,
  Plus,
  ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import { NOTICE_HTML_CLASSES, toSafeNoticeHtml } from "@/lib/noticeHtml";
import {
  AGREEMENT_CULTURES,
  AGREEMENT_CULTURE_LABELS,
  AGREEMENT_TYPES,
  AGREEMENT_TYPE_HINTS,
  AGREEMENT_TYPE_LABELS,
  adminAgreementService,
  type AgreementCulture,
  type AgreementRevisionDto,
  type AgreementType,
} from "@/services/admin-agreement.service";

// The client caches feature configuration, and the backend keeps each evaluated flag for up to
// 60 seconds, so a change here reaches customers on their next configuration fetch rather than
// instantly.
const PROPAGATION_NOTE =
  "Applies once the server cache expires and clients refresh their configuration.";

/**
 * Authoring screen for the commitments tenants are asked to agree to.
 *
 * Content is never edited in place. Publishing always writes a new revision, so an acceptance
 * recorded earlier keeps pointing at wording that can still be read back exactly as it was shown.
 * The "ask everyone again" switch decides whether that new revision re-prompts tenants who had
 * already agreed — off is for corrections (a typo, a broken link).
 */
/**
 * The text in one language, or undefined when that language was not published.
 *
 * `?? []` because a server serving an older shape returns no `translations` at all, and an
 * unguarded `.find` here took the whole Platform Settings page down with it rather than this one
 * card. The card below says so instead.
 */
function translationOf(revision: AgreementRevisionDto, culture: string) {
  return (revision.translations ?? []).find(
    (entry) => entry.culture === culture,
  );
}

/** The default language, falling back to whatever was published — used for headings and previews. */
function primaryTranslation(revision: AgreementRevisionDto) {
  return translationOf(revision, "vi") ?? (revision.translations ?? [])[0];
}

export function AgreementsTab() {
  const queryClient = useQueryClient();
  const [publishFor, setPublishFor] = useState<AgreementType | null>(null);
  const [pendingToggle, setPendingToggle] = useState<{
    type: AgreementType;
    next: boolean;
  } | null>(null);

  const agreementsQuery = useQuery({
    queryKey: ["admin", "agreements"],
    queryFn: () => adminAgreementService.getAgreements(),
  });

  const byType = useMemo(() => {
    const map = new Map<AgreementType, AgreementRevisionDto[]>();
    for (const type of AGREEMENT_TYPES) {
      map.set(type, []);
    }
    for (const revision of agreementsQuery.data ?? []) {
      map.get(revision.type)?.push(revision);
    }
    return map;
  }, [agreementsQuery.data]);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["admin", "agreements"] });

  const setActiveMutation = useMutation({
    mutationFn: ({ type, next }: { type: AgreementType; next: boolean }) =>
      adminAgreementService.setActive(type, next),
    onSuccess: (_data, variables) => {
      void invalidate();
      toast.success(
        variables.next
          ? `Tenants will be asked to accept this commitment. ${PROPAGATION_NOTE}`
          : `Tenants will no longer be asked. Recorded acceptances are kept. ${PROPAGATION_NOTE}`,
      );
      setPendingToggle(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (agreementsQuery.isLoading) {
    return (
      <div className="space-y-3">
        {AGREEMENT_TYPES.map((type) => (
          <div
            key={type}
            className="h-40 animate-pulse rounded-lg bg-muted/60"
          />
        ))}
      </div>
    );
  }

  if (agreementsQuery.error) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
        <p className="text-sm text-muted-foreground">
          Failed to load the commitments.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void agreementsQuery.refetch()}
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
        <ShieldAlert className="mt-0.5 size-4 shrink-0 text-amber-600" />
        <div className="space-y-1 text-sm">
          <p className="font-medium">
            Each commitment has its own feature flag, and both start off.
          </p>
          <p className="text-muted-foreground">
            Turn them on under Feature Flags — “Commitment — After Sign-up” and
            “Commitment — Before Creating a Machine” switch independently. While
            a flag is off that commitment is never shown, never enforced and no
            acceptance for it is recorded, so the text below can be drafted and
            reviewed in peace.
          </p>
        </div>
      </div>

      {AGREEMENT_TYPES.map((type) => {
        const revisions = byType.get(type) ?? [];
        const current = revisions.find((revision) => revision.isCurrent);
        const history = revisions.filter((revision) => !revision.isCurrent);

        return (
          <Card key={type} className="shadow-none">
            <CardHeader className="gap-2">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 space-y-1">
                  <CardTitle className="flex flex-wrap items-center gap-2 text-base">
                    <FileText className="size-4 text-muted-foreground" />
                    {AGREEMENT_TYPE_LABELS[type]}
                    {current ? (
                      <Badge variant="outline">v{current.version}</Badge>
                    ) : (
                      <Badge variant="secondary">Not written yet</Badge>
                    )}
                    {current?.isActive && (
                      <Badge className="bg-emerald-600 text-white hover:bg-emerald-600">
                        Asking
                      </Badge>
                    )}
                  </CardTitle>
                  <CardDescription>
                    {AGREEMENT_TYPE_HINTS[type]}
                  </CardDescription>
                </div>

                <div className="flex shrink-0 items-center gap-3">
                  {current && (
                    <div className="flex items-center gap-2">
                      <Label
                        htmlFor={`active-${type}`}
                        className="cursor-pointer text-sm"
                      >
                        Ask tenants
                      </Label>
                      <Switch
                        id={`active-${type}`}
                        checked={current.isActive}
                        onCheckedChange={(next) =>
                          setPendingToggle({ type, next })
                        }
                        disabled={setActiveMutation.isPending}
                      />
                    </div>
                  )}
                  <Button
                    size="sm"
                    variant={current ? "outline" : "default"}
                    className="gap-2"
                    onClick={() => setPublishFor(type)}
                  >
                    {current ? (
                      <Pencil className="size-4" />
                    ) : (
                      <Plus className="size-4" />
                    )}
                    {current ? "Edit" : "Write it"}
                  </Button>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              {current ? (
                <>
                  <div className="rounded-md border p-4">
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold">
                        {primaryTranslation(current)?.title}
                      </p>
                      {/* Which languages this revision was published in. A customer only gets a
                          language switch for the ones listed here. */}
                      {(current.translations ?? []).map((entry) => (
                        <Badge key={entry.culture} variant="secondary">
                          {AGREEMENT_CULTURE_LABELS[entry.culture] ??
                            entry.culture}
                        </Badge>
                      ))}
                    </div>
                    {primaryTranslation(current) ? (
                      <div
                        className={`text-sm ${NOTICE_HTML_CLASSES}`}
                        // Sanitised through the same allowlist the client console uses, so this
                        // preview shows exactly what the customer will see.
                        dangerouslySetInnerHTML={{
                          __html: toSafeNoticeHtml(
                            primaryTranslation(current)?.bodyHtml ?? "",
                          ),
                        }}
                      />
                    ) : (
                      <p className="text-sm text-amber-700 dark:text-amber-400">
                        This revision's text could not be read. The API is
                        probably serving an older version than this console
                        expects — restart it and reload.
                      </p>
                    )}
                  </div>

                  <dl className="grid gap-x-6 gap-y-1 text-xs text-muted-foreground sm:grid-cols-2">
                    <div className="flex gap-2">
                      <dt>Published</dt>
                      <dd className="text-foreground">
                        {new Date(current.publishedAt).toLocaleString()}
                      </dd>
                    </div>
                    <div className="flex gap-2">
                      <dt>Re-prompts below version</dt>
                      <dd className="text-foreground">
                        {current.minAcceptedVersion}
                      </dd>
                    </div>
                    <div className="flex gap-2 sm:col-span-2">
                      <dt>Content hash</dt>
                      <dd className="truncate font-mono text-foreground">
                        {current.contentHash}
                      </dd>
                    </div>
                  </dl>
                </>
              ) : (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  No text has been published for this commitment yet, so nobody
                  is asked for it.
                </p>
              )}

              {history.length > 0 && (
                <div className="rounded-md border">
                  <p className="flex items-center gap-2 border-b px-3 py-2 text-xs font-medium text-muted-foreground">
                    <History className="size-3.5" />
                    Earlier versions ({history.length}) — kept so past
                    acceptances still point at the wording that was shown
                  </p>
                  <ul className="divide-y">
                    {history.map((revision) => (
                      <li
                        key={revision.id}
                        className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-xs"
                      >
                        <Badge variant="outline">v{revision.version}</Badge>
                        <span className="min-w-0 flex-1 truncate">
                          {primaryTranslation(revision)?.title}
                        </span>
                        <span className="text-muted-foreground">
                          {new Date(revision.publishedAt).toLocaleDateString()}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      <PublishDialog
        type={publishFor}
        current={
          publishFor
            ? (byType.get(publishFor) ?? []).find((r) => r.isCurrent)
            : undefined
        }
        onClose={() => setPublishFor(null)}
        onPublished={() => {
          void invalidate();
          setPublishFor(null);
        }}
      />

      <AlertDialog
        open={pendingToggle !== null}
        onOpenChange={(open) => !open && setPendingToggle(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingToggle?.next
                ? "Start asking tenants to accept this?"
                : "Stop asking tenants to accept this?"}
            </AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            <AlertDialogDescription>
              {pendingToggle?.next
                ? "Every tenant that has not accepted the current version will be asked before they can continue. Machine creation is refused until they do."
                : "Nobody will be asked, and machine creation stops being gated by this commitment. Acceptances already recorded are kept exactly as they are."}
            </AlertDialogDescription>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel className="cursor-pointer">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="cursor-pointer"
              onClick={() =>
                pendingToggle && setActiveMutation.mutate(pendingToggle)
              }
            >
              {setActiveMutation.isPending && (
                <Loader2 className="mr-2 size-4 animate-spin" />
              )}
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface PublishDialogProps {
  type: AgreementType | null;
  current: AgreementRevisionDto | undefined;
  onClose: () => void;
  onPublished: () => void;
}

/** What the editor holds per language while it is open. */
type Draft = Record<string, { title: string; bodyHtml: string }>;

function emptyDraft(): Draft {
  return Object.fromEntries(
    AGREEMENT_CULTURES.map((culture) => [culture, { title: "", bodyHtml: "" }]),
  );
}

/** Pre-fills from the revision being edited; languages it never had start blank. */
function draftFrom(current: AgreementRevisionDto | undefined): Draft {
  const draft = emptyDraft();
  for (const entry of current?.translations ?? []) {
    draft[entry.culture] = { title: entry.title, bodyHtml: entry.bodyHtml };
  }
  return draft;
}

function PublishDialog({
  type,
  current,
  onClose,
  onPublished,
}: PublishDialogProps) {
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [culture, setCulture] = useState<AgreementCulture>(
    AGREEMENT_CULTURES[0],
  );
  const [requireReacceptance, setRequireReacceptance] = useState(true);
  const [openedFor, setOpenedFor] = useState<AgreementType | null>(null);

  // Reset on open rather than through a remount key, so the previous commitment's draft cannot
  // leak into the next one.
  if (type !== null && openedFor !== type) {
    setOpenedFor(type);
    setDraft(draftFrom(current));
    setCulture(AGREEMENT_CULTURES[0]);
    setRequireReacceptance(true);
  }

  const setField = (field: "title" | "bodyHtml", value: string) =>
    setDraft((previous) => ({
      ...previous,
      [culture]: { ...previous[culture], [field]: value },
    }));

  const filledCultures = AGREEMENT_CULTURES.filter(
    (code) =>
      draft[code].title.trim().length > 0 &&
      draft[code].bodyHtml.trim().length > 0,
  );

  const publishMutation = useMutation({
    mutationFn: () =>
      adminAgreementService.publish({
        type: type!,
        // Only the languages actually filled in are sent. A half-written English draft would
        // otherwise be published as an empty commitment, and a customer who switched to it would
        // be asked to agree to nothing.
        translations: filledCultures.map((code) => ({
          culture: code,
          title: draft[code].title.trim(),
          bodyHtml: draft[code].bodyHtml,
        })),
        requireReacceptance,
      }),
    onSuccess: (revision) => {
      toast.success(
        requireReacceptance
          ? `Published v${revision.version}. Everyone will be asked to accept it again.`
          : `Published v${revision.version} as a correction. People who already accepted are not asked again.`,
      );

      // The server reduces the markup to the supported tags before storing and hashing it, so
      // what was stored is what customers read. Said out loud here because the alternative is an
      // operator losing a table and only finding out from a customer.
      if (revision.contentWasReduced) {
        toast.warning(
          "Unsupported markup was removed before saving. Open the commitment to check what was kept.",
          { duration: 10000 },
        );
      }

      onPublished();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // The default language is mandatory — the client falls back to it, so a revision without it
  // would leave a customer with an empty dialog they cannot clear.
  const canSubmit = filledCultures.includes(AGREEMENT_CULTURES[0]);
  const active = draft[culture];

  return (
    <Dialog open={type !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {current
              ? `Publish version ${current.version + 1}`
              : "Publish the first version"}
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <DialogDescription>
            {type ? AGREEMENT_TYPE_HINTS[type] : ""} Text is never edited in
            place — saving always creates a new version, so earlier acceptances
            keep pointing at what was actually shown.
          </DialogDescription>
          {/* Both languages are one document with one version number: a customer reading the
              English agrees to the same revision as one reading the Vietnamese. */}
          <div className="flex flex-wrap items-center gap-2">
            {AGREEMENT_CULTURES.map((code) => {
              const filled = filledCultures.includes(code);
              return (
                <Button
                  key={code}
                  type="button"
                  size="sm"
                  variant={code === culture ? "default" : "outline"}
                  className="h-8 min-h-8 gap-2"
                  onClick={() => setCulture(code)}
                >
                  {AGREEMENT_CULTURE_LABELS[code] ?? code}
                  {code === AGREEMENT_CULTURES[0] ? (
                    <Badge variant="secondary" className="px-1 text-[10px]">
                      required
                    </Badge>
                  ) : (
                    filled && (
                      <Badge variant="secondary" className="px-1 text-[10px]">
                        filled
                      </Badge>
                    )
                  )}
                </Button>
              );
            })}
            <p className="text-xs text-muted-foreground">
              Leave a language empty to publish without it — the customer then
              sees no switch.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="agreement-title">Heading</Label>
            <Input
              id="agreement-title"
              value={active.title}
              onChange={(event) => setField("title", event.target.value)}
              maxLength={300}
              placeholder="e.g. Cam kết sử dụng dịch vụ"
              className="placeholder:text-placeholder"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="agreement-body">Content (HTML)</Label>
            <Textarea
              id="agreement-body"
              value={active.bodyHtml}
              onChange={(event) => setField("bodyHtml", event.target.value)}
              rows={12}
              placeholder="<p>…</p>"
              className="font-mono text-xs placeholder:text-placeholder"
            />
            <p className="text-xs text-muted-foreground">
              Only paragraphs, lists, links and inline formatting survive
              (&lt;p&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;li&gt;, &lt;a&gt;,
              &lt;strong&gt;, &lt;em&gt;, &lt;u&gt;, &lt;code&gt;). Scripts,
              styles, images and iframes are stripped before anything is
              rendered.
            </p>
          </div>

          {active.bodyHtml.trim().length > 0 && (
            <div className="space-y-1.5">
              <Label>Preview — exactly what the customer sees</Label>
              <div className="rounded-md border bg-muted/30 p-4">
                {active.title.trim().length > 0 && (
                  <p className="mb-2 text-sm font-semibold">{active.title}</p>
                )}
                <div
                  className={`text-sm ${NOTICE_HTML_CLASSES}`}
                  dangerouslySetInnerHTML={{
                    __html: toSafeNoticeHtml(active.bodyHtml),
                  }}
                />
              </div>
            </div>
          )}

          {current && (
            <div className="flex items-start justify-between gap-4 rounded-md border p-3">
              <div className="space-y-0.5">
                <Label
                  htmlFor="require-reacceptance"
                  className="cursor-pointer text-sm"
                >
                  Ask everyone to accept again
                </Label>
                <p className="text-xs text-muted-foreground">
                  Leave this on for any change of substance. Turn it off only
                  for a correction — a typo or a broken link — where people who
                  already accepted should not be interrupted.
                </p>
              </div>
              <Switch
                id="require-reacceptance"
                checked={requireReacceptance}
                onCheckedChange={setRequireReacceptance}
              />
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!canSubmit || publishMutation.isPending}
            onClick={() => publishMutation.mutate()}
          >
            {publishMutation.isPending && (
              <Loader2 className="mr-2 size-4 animate-spin" />
            )}
            Publish
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
